import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { SearchResponse } from '../types';

const PAGE_SIZE = 30;
// Backend validator caps offset at 5000; mirror that here so the pager
// doesn't render pages a request couldn't actually reach.
const MAX_OFFSET = 5000;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get('q') ?? '';
  const archiveId = params.get('archive_id');
  const archiveDomain = params.get('archive_domain');
  const pageParam = Math.max(1, Number(params.get('page') ?? '1') | 0);

  const [draft, setDraft] = useState(initialQ);
  const [showTips, setShowTips] = useState(false);
  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  useEffect(() => {
    const q = (params.get('q') ?? '').trim();
    if (q.length < 2) {
      setData(null);
      setError(null);
      return;
    }
    const myReq = ++reqRef.current;
    setLoading(true);
    setError(null);
    const offset = (pageParam - 1) * PAGE_SIZE;
    const query: Record<string, string | number> = { q, limit: PAGE_SIZE, offset };
    if (archiveId) query.archive_id = archiveId;

    api<SearchResponse>('/search', { query })
      .then((resp) => {
        if (myReq !== reqRef.current) return;
        setData(resp);
      })
      .catch((e) => {
        if (myReq !== reqRef.current) return;
        if (e instanceof ApiError) {
          const payload = e.payload as { errors?: Record<string, string[]>; message?: string } | null;
          const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
          setError(first ?? payload?.message ?? `Search failed (${e.status}).`);
        } else {
          setError('Search failed.');
        }
        setData(null);
      })
      .finally(() => {
        if (myReq === reqRef.current) setLoading(false);
      });
  }, [params, archiveId, pageParam]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    const next = new URLSearchParams(params);
    if (q) next.set('q', q); else next.delete('q');
    // New query → back to page 1.
    next.delete('page');
    setParams(next, { replace: false });
  }

  function clearArchiveFilter() {
    const next = new URLSearchParams(params);
    next.delete('archive_id');
    next.delete('archive_domain');
    next.delete('page');
    setParams(next, { replace: false });
  }

  function goToPage(p: number) {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page'); else next.set('page', String(p));
    setParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const q = (params.get('q') ?? '').trim();
  const total = data?.meta.total ?? 0;
  const reachableLastPage = Math.floor(MAX_OFFSET / PAGE_SIZE) + 1;
  const lastPage = total > 0 ? Math.min(reachableLastPage, Math.ceil(total / PAGE_SIZE)) : 1;
  const showingFrom = data && data.data.length > 0 ? data.meta.offset + 1 : 0;
  const showingTo = data ? data.meta.offset + data.data.length : 0;

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Search</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Full-text search across the latest snapshot of every URL in
          {archiveId ? ' the selected archive' : ' every indexed archive'}.
          {' '}Supports <code>"exact phrase"</code>, <code>-exclude</code>,
          {' '}<code>site:example.com</code>, <code>intitle:word</code>.
          {' '}
          <button
            type="button"
            className="link"
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--link, #36c)', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
            onClick={() => setShowTips((s) => !s)}
          >
            {showTips ? 'Hide tips' : 'Search tips'}
          </button>
        </p>

        {showTips && <SearchTips />}

        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder='Search archived content… try "exact phrase" or site:example.com'
            autoFocus
            style={{ flex: '1 1 320px', minWidth: 0 }}
          />
          <button type="submit" disabled={!draft.trim() || draft.trim().length < 2}>Search</button>
        </form>

        {archiveId && (
          <p style={{ fontSize: 13, marginTop: 8 }}>
            <span className="muted">Filtering to:</span>{' '}
            <strong>{archiveDomain ?? `archive #${archiveId}`}</strong>
            {' · '}
            <button
              type="button"
              className="secondary"
              style={{ padding: '2px 8px', fontSize: 12 }}
              onClick={clearArchiveFilter}
            >
              Search all archives instead
            </button>
          </p>
        )}

        {data && <ParsedChips parsed={data.meta.parsed} />}
      </div>

      <div className="card">
        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
        {loading && <p className="muted">Searching…</p>}
        {!loading && q.length < 2 && (
          <p className="muted">Enter a query above to search.</p>
        )}
        {!loading && q.length >= 2 && data && data.data.length === 0 && (
          <p className="muted">
            No results for <strong>{q}</strong>.
            {archiveId ? ' Try removing the archive filter,' : ''} or check
            that the relevant archives have been indexed (admins see an
            "Index for search" button on each archive page).
          </p>
        )}
        {!loading && data && data.data.length > 0 && (
          <>
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of {total.toLocaleString()}
              {data.meta.capped && ' (capped)'}
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {data.data.map((hit) => (
                <li key={`${hit.archive_id}:${hit.snapshot_id}`} style={{ marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #eee' }}>
                  <div style={{ fontSize: 13 }}>
                    <Link to={`/archives/${hit.archive_id}`} className="muted">{hit.archive_domain}</Link>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, margin: '2px 0' }}>
                    <a
                      href={`/api/replay/${hit.archive_id}/${encodeURIComponent(hit.captured_at)}/${encodeURIComponent(hit.url)}`}
                      target="_blank"
                      rel="noopener"
                    >
                      {hit.title
                        // Manticore returns the title pre-highlighted
                        // with <mark> wrappers around matched terms;
                        // the backend HTML-escapes everything else, so
                        // only <mark>/</mark> are live markup.
                        ? <span dangerouslySetInnerHTML={{ __html: hit.title }} />
                        : hit.url}
                    </a>
                  </div>
                  <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>{hit.url}</div>
                  <div
                    style={{ fontSize: 13, marginTop: 6, lineHeight: 1.4 }}
                    dangerouslySetInnerHTML={{ __html: hit.snippet }}
                  />
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Captured {new Date(hit.captured_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>

            {lastPage > 1 && (
              <Pager page={pageParam} lastPage={lastPage} onGo={goToPage} />
            )}
          </>
        )}
      </div>
    </>
  );
}

function SearchTips() {
  return (
    <div style={{
      fontSize: 13, marginBottom: 12, padding: '10px 12px',
      background: 'var(--card-alt-bg, #f7f7f7)',
      borderRadius: 4, lineHeight: 1.5,
    }}>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>Query operators</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li><code>cat dog</code> — both <em>cat</em> AND <em>dog</em> must appear (default)</li>
        <li><code>"social archive"</code> — exact phrase</li>
        <li><code>cat -dog</code> — <em>cat</em> must appear, <em>dog</em> must not</li>
        <li><code>cat site:example.com</code> — restrict to archives on example.com (subdomains included)</li>
        <li><code>intitle:tutorial</code> — word must appear in the page title</li>
      </ul>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        Words under 4 characters and common stopwords are ignored by the
        full-text index. Wildcards (<code>cat*</code>) aren't yet supported.
      </div>
    </div>
  );
}

function ParsedChips({ parsed }: { parsed: SearchResponse['meta']['parsed'] }) {
  const chips: { label: string; value: string }[] = [];
  if (parsed.site) chips.push({ label: 'site', value: parsed.site });
  if (parsed.intitle) chips.push({ label: 'in title', value: parsed.intitle.replace(/^[+\-]/, '') });
  for (const p of parsed.phrases) chips.push({ label: 'phrase', value: `"${p}"` });
  if (chips.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, fontSize: 12 }}>
      {chips.map((c, i) => (
        <span key={i} style={{
          background: 'var(--chip-bg, #eef)',
          color: 'var(--chip-fg, #224)',
          padding: '2px 8px',
          borderRadius: 12,
          whiteSpace: 'nowrap',
        }}>
          <span className="muted" style={{ marginRight: 4 }}>{c.label}:</span>
          {c.value}
        </span>
      ))}
    </div>
  );
}

function Pager({ page, lastPage, onGo }: { page: number; lastPage: number; onGo: (p: number) => void }) {
  // Compact pager: Prev · 1 … window … last · Next. Window is current
  // ±2 so big result sets don't render hundreds of links.
  const windowSize = 2;
  const start = Math.max(2, page - windowSize);
  const end = Math.min(lastPage - 1, page + windowSize);
  const items: (number | '…')[] = [1];
  if (start > 2) items.push('…');
  for (let i = start; i <= end; i++) items.push(i);
  if (end < lastPage - 1) items.push('…');
  if (lastPage > 1) items.push(lastPage);

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(1)} title="First page">« First</button>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(page - 1)}>Prev</button>
      {items.map((it, idx) =>
        it === '…' ? (
          <span key={`g${idx}`} className="muted" style={{ padding: '0 4px' }}>…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={it === page ? '' : 'secondary'}
            onClick={() => onGo(it)}
            style={{ minWidth: 32 }}
          >
            {it}
          </button>
        ),
      )}
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(page + 1)}>Next</button>
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(lastPage)} title="Last page">Last »</button>
    </div>
  );
}
